/**
 * ARCHAT — Frontend Runtime Monitor
 * Lightweight in-memory observability for the PWA.
 * No Sentry. No external analytics. No PII. No message content.
 */

const MAX_EVENTS = 200;
const BUILD_ID = (typeof window !== 'undefined' && window.__ARCHAT_BUILD_ID__) || (import.meta.env?.VITE_BUILD_ID) || 'unknown';

const eventBuffer = [];
let reconectCount = 0;
let lastReconnectTs = 0;
let reconnectRate = 0; // reconnects per minute (smoothed)
let isOffline = navigator.onLine !== false;
let isVisible = document.visibilityState === 'visible';
let activeSockets = 0;

// ─── Circular Event Buffer ───────────────────────────────────────────────────

function pushEvent(type, severity = 'info', extra = {}) {
  const event = {
    ts: new Date().toISOString(),
    type,
    severity,
    build_id: BUILD_ID,
    route: window.location ? window.location.pathname : '',
    language: navigator.language || '',
    online: navigator.onLine !== false,
    visibility: document.visibilityState,
    ...extra,
  };
  eventBuffer.push(event);
  if (eventBuffer.length > MAX_EVENTS) eventBuffer.shift();
  return event;
}

// ─── Global Error Capture ────────────────────────────────────────────────────

function setupGlobalErrorCapture() {
  // window.onerror
  const origOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    pushEvent('window_error', 'error', {
      msg: String(message).slice(0, 200),
      source: source ? source.slice(0, 100) : null,
      line: lineno,
    });
    if (typeof origOnError === 'function') origOnError(message, source, lineno, colno, error);
  };

  // unhandledrejection
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const msg = reason?.message || String(reason) || 'unknown';
    pushEvent('unhandled_rejection', 'error', { msg: msg.slice(0, 200) });
  });
}

// ─── Reconnect Monitor ───────────────────────────────────────────────────────

function recordReconnect() {
  reconectCount++;
  const now = Date.now();
  if (lastReconnectTs > 0) {
    const deltaMs = now - lastReconnectTs;
    // Exponential moving average for reconnect rate
    const instantRate = 60000 / Math.max(deltaMs, 100); // reconnects per minute based on this interval
    reconnectRate = reconnectRate * 0.7 + instantRate * 0.3;
  }
  lastReconnectTs = now;

  // Detect storm: > 10 reconnects per minute smoothed
  if (reconnectRate > 10) {
    pushEvent('reconnect_storm', 'warn', {
      reconnect_rate: Math.round(reconnectRate * 10) / 10,
      total_reconnects: reconectCount,
    });
  } else if (reconnectRate > 3) {
    pushEvent('reconnect_flap', 'warn', {
      reconnect_rate: Math.round(reconnectRate * 10) / 10,
      total_reconnects: reconectCount,
    });
  }
}

function setActiveSockets(count) {
  activeSockets = count;
}

// ─── Online / Visibility ─────────────────────────────────────────────────────

function setupNetworkCapture() {
  window.addEventListener('online', () => {
    isOffline = false;
    pushEvent('network_online', 'info');
  });
  window.addEventListener('offline', () => {
    isOffline = true;
    pushEvent('network_offline', 'warn');
  });
  document.addEventListener('visibilitychange', () => {
    isVisible = document.visibilityState === 'visible';
    if (isVisible) {
      pushEvent('visibility_visible', 'info');
    }
  });
}

// ─── API ─────────────────────────────────────────────────────────────────────

/**
 * Register a React render crash in the event buffer.
 * Called by ErrorBoundary.componentDidCatch.
 */
function captureRenderCrash(error, errorInfo) {
  pushEvent('react_crash', 'error', {
    msg: error?.message?.slice(0, 200),
    stack: error?.stack?.split('\n').slice(0, 3).join(' | ').slice(0, 300),
  });
}

/**
 * Record a fetch failure.
 */
function captureFetchFailure(url, status, method) {
  pushEvent('fetch_failure', 'error', {
    url: url ? url.slice(0, 100) : null,
    status,
    method,
  });
}

/**
 * Record a translation rendering failure.
 */
function captureTranslationFailure(lang, key) {
  pushEvent('translation_failure', 'warn', {
    lang,
    key: key ? key.slice(0, 100) : null,
  });
}

// ─── Debug Endpoint ──────────────────────────────────────────────────────────

function setupDebugEndpoint() {
  if (typeof window === 'undefined') return;
  window.__ARCHAT_RUNTIME_DEBUG__ = {
    getEvents: (count = 50) => eventBuffer.slice(-count),
    getReconnectState: () => ({
      total_reconnects: reconectCount,
      reconnect_rate_per_min: Math.round(reconnectRate * 10) / 10,
      last_reconnect_ago_ms: lastReconnectTs > 0 ? Date.now() - lastReconnectTs : null,
    }),
    getRuntimeState: () => ({
      build_id: BUILD_ID,
      online: navigator.onLine !== false,
      visibility: document.visibilityState,
      active_sockets: activeSockets,
      total_events: eventBuffer.length,
      max_events: MAX_EVENTS,
    }),
    getEventsByType: (type, count = 50) =>
      eventBuffer.filter(e => e.type === type).slice(-count),
    clearEvents: () => { eventBuffer.length = 0; },
  };
}

// ─── Init ────────────────────────────────────────────────────────────────────

let initialized = false;
export function initRuntimeMonitor() {
  if (initialized) return;
  initialized = true;
  setupGlobalErrorCapture();
  setupNetworkCapture();
  setupDebugEndpoint();
  pushEvent('monitor_start', 'info');
}

export {
  pushEvent,
  captureRenderCrash,
  captureFetchFailure,
  captureTranslationFailure,
  recordReconnect,
  setActiveSockets,
  eventBuffer,
};
